import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  IconButton,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  FormControlLabel,
  Checkbox,
  TextField
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const SetupPracticeReviewDialog = ({ 
  open, 
  onClose, 
  request, 
  onApprove, 
  onCancel,
  loading 
}) => {
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  // State for tax exemption confirmation
  // Radio selection: null until chosen, then boolean
  const [confirmTaxExempt, setConfirmTaxExempt] = useState(null);
  // State: approver allows on-site collections for this practice
  const [confirmAllowOnsite, setConfirmAllowOnsite] = useState(false);
  // Optional QuickBooks Customer ID to set on organization upon approval
  const [qbId, setQbId] = useState('');
  // Reset selection whenever dialog opens
  useEffect(() => {
    if (open) {
      setConfirmTaxExempt(null);
      setConfirmAllowOnsite(false);
      setQbId('');
    }
  }, [open]);

  if (!request) return null;

  const handleApproveConfirm = () => {
    setShowApproveConfirm(false);
    // Pass tax exemption and in-house collection allowance to approval handler
    onApprove(confirmTaxExempt, confirmAllowOnsite, qbId);
  };

  const handleCancelConfirm = () => {
    setShowCancelConfirm(false);
    onCancel();
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          Review setup practice request
          <IconButton
            aria-label="close"
            onClick={onClose}
            disabled={loading}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Provider information
            </Typography>
            <Box sx={{ pl: 2 }}>
              <Typography variant="body1">
                <strong>Name:</strong> {`${request.provider_name_prefix || ''} ${request.provider_first_name || ''} ${request.provider_middle_name || ''} ${request.provider_last_name || ''} ${request.provider_name_suffix || ''}`.trim()}
              </Typography>
              <Typography variant="body1">
                <strong>NPI:</strong> {request.provider_npi}
              </Typography>
              <Typography variant="body1">
                <strong>Email:</strong> {request.practice_email}
              </Typography>
              <Typography variant="body1">
                <strong>Phone:</strong> {request.provider_phone}
              </Typography>
              <Typography variant="body1">
                <strong>Fax:</strong> {request.provider_fax}
              </Typography>
              <Typography variant="body1">
                <strong>Credential:</strong> {request.provider_credential}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Practice information
            </Typography>
            <Box sx={{ pl: 2 }}>
              <Typography variant="body1">
                <strong>Practice Name (Legal Name):</strong> {request.practice_name}
              </Typography>
              <Typography variant="body1">
                <strong>NPI:</strong> {request.practice_npi}
              </Typography>
              <Typography variant="body1">
                <strong>DBA:</strong> {request.practice_dba}
              </Typography>
              <Typography variant="body1">
                <strong>EIN:</strong> {request.practice_ein}
              </Typography>
              <Typography variant="body1">
                <strong>State Tax ID:</strong> {request.practice_state_tax_id}
              </Typography>
              <Typography variant="body1">
                <strong>Practice Type:</strong> {request.practice_type}
              </Typography>
              <Typography variant="body1">
                <strong>Corporate Address:</strong> {request.corporate_street_1} {request.corporate_street_2}
              </Typography>
              <Typography variant="body1">
                <strong>City, State, ZIP:</strong> {request.corporate_city}, {request.corporate_state} {request.corporate_zip_code}
              </Typography>
              <Typography variant="body1">
                <strong>Billing Address:</strong> {request.billing_street_1} {request.billing_street_2}
              </Typography>
              <Typography variant="body1">
                <strong>City, State, ZIP:</strong> {request.billing_city}, {request.billing_state} {request.billing_zip_code}
              </Typography>
              <Typography variant="body1">
                <strong>Shipping Address:</strong> {request.shipping_street_1} {request.shipping_street_2}
              </Typography>
              <Typography variant="body1">
                <strong>City, State, ZIP:</strong> {request.shipping_city}, {request.shipping_state} {request.shipping_zip_code}
              </Typography>
              <Typography variant="body1">
                <strong>Phone:</strong> {request.practice_phone}
              </Typography>
              <Typography variant="body1">
                <strong>Fax:</strong> {request.practice_fax}
              </Typography>
              <Typography variant="body1">
                <strong>Email:</strong> {request.practice_email}
              </Typography>
              <Typography variant="body1">
                <strong>Website:</strong> {request.practice_website}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        {/* Input Section: Tax Exemption and On-site Collections */}
        <Box sx={{ px: 3, pt: 2, pb: 2 }}>
          {request.practice_request_tax_exemption && (
            <Typography variant="body1">
              <strong>Tax exemption requested:</strong> Yes
            </Typography>
          )}
          <FormControl component="fieldset" sx={{ mt: 2 }}>
            <FormLabel component="legend">Tax exemption status</FormLabel>
            <RadioGroup
              name="tax-exempt-status"
              value={confirmTaxExempt === null ? '' : confirmTaxExempt ? 'yes' : 'no'}
              onChange={(e) => setConfirmTaxExempt(e.target.value === 'yes')}
            >
              <FormControlLabel
                value="no"
                control={<Radio />}
                label="Not tax exempt"
              />
              <FormControlLabel
                value="yes"
                control={<Radio />}
                label="Tax exempt, and we have collected the documentation"
              />
            </RadioGroup>
          </FormControl>
          {/* On-site collections section */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>On-site collections intent:</strong> {request.practice_is_doing_in_house_collection ? 'Yes' : 'No'}
            </Typography>
            {request.practice_is_doing_in_house_collection && (
              <>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Agreement key: {request.agreement_key_for_in_house_collection || 'n/a'}
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={confirmAllowOnsite}
                      onChange={(e) => setConfirmAllowOnsite(e.target.checked)}
                    />
                  }
                  label="Approve on-site collections for this organization"
                />
              </>
            )}
          </Box>
          {/* Optional QuickBooks Customer ID (Manager only context) under tax controls */}
          <Box sx={{ mt: 2 }}>
            <FormLabel component="legend">QuickBooks Customer ID (optional)</FormLabel>
            <Typography variant="caption" color="text.secondary">
              Set this value for existing customers with a quickbook id
            </Typography>
            <Box sx={{ mt: 1 }}>
              <TextField
                label="QuickBooks Customer ID"
                placeholder="e.g. 12345"
                value={qbId}
                onChange={(e) => setQbId(e.target.value)}
                variant="standard"
                size="small"
                sx={{ width: 320, maxWidth: '100%' }}
              />
            </Box>
          </Box>
        </Box>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3 }}>
          <Button 
            onClick={() => setShowCancelConfirm(true)} 
            color="error"
            variant="outlined"
            disabled={loading}
            sx={{
              color: '#000000',
              borderColor: (theme) => theme.palette.error.main,
              '&:hover': {
                borderColor: (theme) => theme.palette.error.main,
                backgroundColor: 'rgba(0,0,0,0.04)'
              }
            }}
          >
            {loading ? <CircularProgress size={24} /> : 'Cancel Request'}
          </Button>
          <Button
            onClick={() => setShowApproveConfirm(true)}
            color="primary"
            variant="contained"
            disabled={loading || confirmTaxExempt === null}
          >
            {loading ? <CircularProgress size={24} /> : 'Approve'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approve Confirmation Dialog */}
      <Dialog
        open={showApproveConfirm}
        onClose={() => setShowApproveConfirm(false)}
      >
        <DialogTitle>Confirm approval</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to approve this practice setup request? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowApproveConfirm(false)}>Cancel</Button>
          <Button onClick={handleApproveConfirm} color="primary" variant="contained">
            Approve
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Confirmation Dialog */}
      <Dialog
        open={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
      >
        <DialogTitle>Confirm cancellation</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to cancel this practice setup request? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCancelConfirm(false)}>Cancel</Button>
          <Button onClick={handleCancelConfirm} color="error" variant="contained">
            Cancel Request
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SetupPracticeReviewDialog; 
